import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, Clock, Package } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { CURRENCY, tStep } from "@/lib/constants";
import { useLang } from "@/context/LanguageContext";

const C = {
  fr: { title: "Suivi de mes dossiers", subtitle: "Suivez l'avancement de chaque étape en temps réel.",
    none: "Aucun dossier à suivre", reserve: "Réserver un pack", paid: "Payé", pendingPay: "Paiement en attente",
    startAfter: "Le suivi débute dès la confirmation de votre paiement.", finalize: "Finaliser le paiement",
    progress: "Avancement du dossier", current: "Étape en cours", advisorNote: "Note de votre conseiller",
    loading: "Chargement…" },
  en: { title: "My file tracking", subtitle: "Track the progress of each step in real time.",
    none: "No file to track", reserve: "Book a package", paid: "Paid", pendingPay: "Payment pending",
    startAfter: "Tracking starts as soon as your payment is confirmed.", finalize: "Complete payment",
    progress: "File progress", current: "Current step", advisorNote: "Note from your advisor",
    loading: "Loading…" },
};

export default function Suivi() {
  const { lang } = useLang();
  const c = C[lang];
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);

  useEffect(() => {
    api.get("/bookings/me").then((r) => setBookings(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const finalize = async (b) => {
    setPayingId(b.id);
    try {
      const { data } = await api.post(`/bookings/${b.id}/checkout`, { pack_id: b.pack_id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail));
      setPayingId(null);
    }
  };

  if (loading) return <p className="text-muted-foreground">{c.loading}</p>;

  return (
    <div data-testid="suivi-page">
      <h1 className="font-heading text-3xl sm:text-4xl text-navy">{c.title}</h1>
      <p className="text-muted-foreground mt-2">{c.subtitle}</p>

      {bookings.length === 0 ? (
        <div className="bg-white border border-border p-10 text-center mt-8">
          <Package size={40} className="mx-auto text-gold" />
          <p className="text-navy font-medium mt-4">{c.none}</p>
          <Link to="/nos-packs" className="inline-block mt-5 bg-gold hover:bg-gold-hover text-white px-6 py-3 font-medium transition-colors">{c.reserve}</Link>
        </div>
      ) : (
        <div className="space-y-6 mt-8">
          {bookings.map((b) => (
            <div key={b.id} data-testid={`suivi-${b.id}`} className="bg-white border border-border p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-6 border-b border-border">
                <div>
                  <h3 className="font-heading text-2xl text-navy">{b.pack_name}</h3>
                  <p className="text-sm text-muted-foreground">{CURRENCY(b.amount)}</p>
                </div>
                {b.payment_status === "paid"
                  ? <span className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-1.5 w-fit"><CheckCircle2 size={15} /> {c.paid}</span>
                  : <span className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 px-3 py-1.5 w-fit"><Clock size={15} /> {c.pendingPay}</span>}
              </div>

              {b.payment_status !== "paid" ? (
                <div className="mt-6 text-sm text-muted-foreground">
                  <p>{c.startAfter}</p>
                  <button
                    data-testid={`finalize-payment-${b.id}`}
                    onClick={() => finalize(b)}
                    disabled={payingId === b.id}
                    className="inline-block mt-3 bg-gold hover:bg-gold-hover text-white px-5 py-2.5 font-medium transition-colors disabled:opacity-60"
                  >
                    {payingId === b.id ? c.loading : c.finalize}
                  </button>
                </div>
              ) : (
                <>
                  {(() => {
                    const pct = Math.round((b.step_index / (b.steps.length - 1)) * 100);
                    return (
                      <div className="mt-6" data-testid={`progress-${b.id}`}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-navy font-medium">{c.progress}</span>
                          <span className="text-gold font-heading text-2xl">{pct}%</span>
                        </div>
                        <div className="mt-2 h-2.5 w-full bg-border rounded-full overflow-hidden">
                          <div className="h-full bg-gold rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })()}
                  <ol className="mt-8 relative border-l-2 border-border ml-3">
                    {b.steps.map((step, idx) => {
                      const done = idx <= b.step_index;
                      const current = idx === b.step_index;
                      return (
                        <li key={step} className="mb-6 ml-6">
                          <span className={`absolute -left-[11px] flex items-center justify-center h-5 w-5 rounded-full ${done ? "bg-gold text-white" : "bg-white border-2 border-border text-transparent"}`}>
                            {done ? <CheckCircle2 size={14} /> : <Circle size={10} />}
                          </span>
                          <h4 className={`text-sm font-medium ${current ? "text-gold" : done ? "text-navy" : "text-muted-foreground"}`}>{tStep(step, lang)}</h4>
                          {current && <p className="text-xs text-muted-foreground mt-0.5">{c.current}</p>}
                        </li>
                      );
                    })}
                  </ol>
                </>
              )}

              {b.admin_note && (
                <div className="mt-4 bg-[#F8F9FA] border border-border p-4 text-sm">
                  <span className="text-gold font-semibold uppercase text-xs tracking-widest">{c.advisorNote}</span>
                  <p className="text-slate-700 mt-1">{b.admin_note}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
